import React from 'react';
import { IconTooltip } from './IconTooltip';

interface ProgressStep {
    status: string;
    label: string;
    icon: React.ReactNode;
}

export interface ProgressTrackerProps {
    title?: string;
    steps: ProgressStep[];
    currentStatusIndex: number;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
    title = 'Order Progress',
    steps,
    currentStatusIndex,
}) => {
    const halfStepPercent = steps.length > 1 ? 100 / (2 * steps.length) : 0;

    return (
        <div className="bg-card rounded-2xl border shadow-sm p-6 overflow-hidden">
            <p className="font-bold text-foreground text-sm uppercase tracking-wider opacity-70 mb-6">
                {title}
            </p>
            <div className="flex items-start justify-between relative">
                {/* Track Line Container aligned precisely between the centers of first and last step circles */}
                <div
                    className="absolute top-5 h-0.5 z-0"
                    style={{
                        left: `${halfStepPercent}%`,
                        right: `${halfStepPercent}%`,
                    }}
                >
                    <div className="w-full h-full bg-secondary/20" />
                    <div
                        className="absolute top-0 left-0 h-full bg-primary transition-all duration-500"
                        style={{
                            width: currentStatusIndex >= 0 && steps.length > 1
                                ? `${(Math.min(currentStatusIndex, steps.length - 1) / (steps.length - 1)) * 100}%`
                                : '0%'
                        }}
                    />
                </div>

                {steps.map((step, index) => {
                    const isCompleted = index < currentStatusIndex;
                    const isActive = index === currentStatusIndex;
                    return (
                        <div key={step.status} className="flex flex-col items-center gap-2 z-10 flex-1">
                            <IconTooltip content={step.label}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                                    isActive
                                        ? 'bg-primary border-primary text-primary-foreground ring-4 ring-primary/20 shadow-md'
                                        : isCompleted
                                            ? 'bg-primary border-primary text-primary-foreground shadow-sm'
                                            : 'bg-card border-secondary/30 text-muted-foreground'
                                }`}>
                                    {step.icon}
                                </div>
                            </IconTooltip>
                            <span className={`text-[10px] font-semibold text-center leading-tight ${
                                isActive ? 'text-primary font-bold' : isCompleted ? 'text-foreground font-medium' : 'text-muted-foreground'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
